import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TaskRecord } from "@nexus/contracts";

import { personaOpportunityHubKeys } from "@/lib/queries/persona-opportunity-hub.queries";
import { invalidatePersonaOpportunitySets } from "@/lib/queries/persona-opportunity-sets.queries";
import { detectFinishedPersonaScriptsGenerationIds } from "../lib/persona-scripts-generation-active";

/**
 * When a generate-persona-scripts or post-flight-double-down task finishes,
 * refetch set list/detail and the hub index immediately instead of waiting for
 * a manual refresh.
 */
export function useInvalidatePersonaDataOnScriptsGenerationTaskEnd(
  tasks: readonly TaskRecord[],
  tenantId: string | null | undefined,
): void {
  const queryClient = useQueryClient();
  const inProgressRef = React.useRef<Set<string>>(new Set());
  const trackedTenantRef = React.useRef(tenantId);

  if (trackedTenantRef.current !== tenantId) {
    trackedTenantRef.current = tenantId;
    inProgressRef.current = new Set();
  }

  React.useEffect(() => {
    const scopedTasks = tenantId
      ? tasks.filter((task) => task.tenantId === tenantId)
      : [];
    const { inProgress, finishedIds } = detectFinishedPersonaScriptsGenerationIds(
      inProgressRef.current,
      scopedTasks,
    );
    inProgressRef.current = inProgress;
    if (finishedIds.length === 0) return;
    invalidatePersonaOpportunitySets(queryClient, tenantId);
    void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
  }, [queryClient, tasks, tenantId]);
}
